import { withTranslation } from 'react-i18next';
import { util } from '@helper';
import React from 'react';
import _ from 'lodash';
import {
  FaHome,
  FaBuilding,
  FaSuitcase,
  FaFileAlt,
  FaUser,
  FaClock,
  FaMapMarkerAlt,
  FaMagic,
} from 'react-icons/fa';
import {
  Col,
  Affix,
  Stack,
  IconButton,
  Grid,
  Row,
  Nav,
  Table,
  Avatar,
  Badge,
  Button,
  FlexboxGrid,
  Panel,
  toaster,
  Pagination,
  Whisper,
  Tooltip,
} from 'rsuite';
import { Promise } from 'bluebird';
import { JobApplicationAPI } from '@services/api';
import InfiniteScroll from 'react-infinite-scroller';
import FlexboxGridItem from 'rsuite/esm/FlexboxGrid/FlexboxGridItem';
import PlaceholderGraph from 'rsuite/esm/Placeholder/PlaceholderGraph';
import constants from '@helper/constants';

const { withRouter } = util;

// The classes below are the Tailwind translation of the former `styles.scss`.
// Every descendant selector is preserved through arbitrary variants
// (`[&_...]`) so the cascade and specificity stay identical to the original
// stylesheet. The remaining class names (e.g. `saved-job-main-content`,
// `search-results-row`, `search-result-card`) are only kept as selector hooks
// for those variants.

/* eslint-disable max-len */
const TABLE_VIEW_CLASS = 'block max-[768px]:!hidden';
const CARD_VIEW_CLASS = 'hidden max-[768px]:block';

const APPLICATION_CARDS_CLASS = 'flex flex-col gap-3';
const CARD_VIEW_STATE_CLASS =
  'flex items-center justify-center px-6 py-12 text-center';

const APPLICATION_CARD_CLASS = [
  '!mb-0 cursor-pointer rounded-2xl',
  '[&_.rs-panel-body]:p-4',
  'max-[425px]:rounded-xl max-[425px]:[&_.rs-panel-body]:p-3',
].join(' ');

const APPLICATION_CARD_HEADER_CLASS = 'flex flex-col items-start gap-2';
const APPLICATION_CARD_TITLE_CLASS =
  'text-[0.95rem] font-semibold text-[#212121] max-[425px]:text-[0.85rem]';
const APPLICATION_CARD_STATUS_CLASS =
  '[&_.btn-tg-pill-shaped]:!mt-0 [&_.btn-tg-pill-shaped]:w-fit';
const APPLICATION_CARD_DIVIDER_CLASS =
  'my-[10px] border-0 border-t border-solid border-t-[#e9e9e9]';
const APPLICATION_CARD_COMPANY_CLASS = 'flex items-center gap-[10px]';
const APPLICATION_CARD_COMPANY_NAME_CLASS = 'text-[0.9rem] text-[#212121]';
const APPLICATION_CARD_DETAILS_CLASS = 'flex flex-col gap-2';
const APPLICATION_CARD_ROW_CLASS = 'flex items-center justify-between';
const APPLICATION_CARD_LABEL_CLASS =
  'mr-3 shrink-0 text-[0.82rem] text-[#757575] max-[425px]:text-[0.75rem]';
const APPLICATION_CARD_VALUE_CLASS =
  'text-right text-[0.85rem] text-[#212121] max-[425px]:text-[0.78rem]';

const MY_APPLICATION_CONTAINER_CLASS = [
  // Table
  '[&_.rs-table-body-row-wrapper]:rounded-b-[20px] [&_.rs-table-body-row-wrapper]:bg-white',
  '[&_.rs-table-cell-header>div[role=columnheader]_.rs-table-cell-content]:text-center',
  '[&_.rs-table-cell-header>div[role=columnheader]_.rs-table-cell-content]:text-[.9rem]',
  '[&_.rs-table-cell-header>div[role=columnheader]_.rs-table-cell-content]:text-[#595959]',
  '[&_.rs-table-row-header]:rounded-t-[20px]',
  '[&_.rs-table-row]:text-center',
  '[&_.rs-table-row_.rs-table-cell-content]:px-[10px] [&_.rs-table-row_.rs-table-cell-content]:py-[28px]',
  '[&_.rs-table-row_.rs-table-cell-content]:text-[.8rem]',
  '[&_.rs-table-row_.rs-table-cell-content]:text-[#212121]',
  '[&_.rs-table-row_.rs-table-cell-content_div.btn-tg-pill-shaped]:!text-[.9em]',
  '[&_.rs-table-row_.rs-avatar]:bg-white [&_.rs-table-row_.rs-avatar]:object-contain',
  '[&_.rs-table-row_.rs-table-cell-last_.rs-table-cell-content]:pr-[20%]',
  '[&_.rs-table-row[role=row]]:cursor-pointer',
  '[&_.rs-table-row[role=row].rs-table-row-header]:cursor-default',
  // Avatar
  '[&_.rs-avatar>.rs-avatar-image]:object-contain',
  // Headings
  '[&_h2]:mb-6 [&_h2]:mt-6 [&_h2]:text-[1.5em]',
  '[&_h3]:text-[1.25em]',
  // Pagination
  '[&_.rs-pagination-group]:pt-6',
  '[&_.rs-pagination-group_.rs-pagination-group-total]:text-[#595959]',
  '[&_.rs-pagination-group_.rs-pagination-group-total]:!text-[.85rem]',
  '[&_.rs-pagination-group_.rs-pagination-group-total_span]:text-[.85rem]',
  '[&_.rs-pagination-group_.rs-pagination-btn]:text-[#595959]',
  '[&_.rs-pagination-group_.rs-pagination-btn]:!text-[.85rem]',
  '[&_.rs-pagination-group_.rs-pagination-btn_span]:text-[.85rem]',
  // Nav tabs
  '[&_.rs-nav]:!mb-8 [&_.rs-nav]:!w-fit [&_.rs-nav]:rounded-[30px] [&_.rs-nav]:bg-white [&_.rs-nav]:p-[10px]',
  'max-[425px]:[&_.rs-nav]:!w-auto',
  'max-[425px]:[&_.rs-nav_a[role=button]]:w-1/3',
  '[&_.rs-nav_.rs-nav-item]:rounded-[30px] [&_.rs-nav_.rs-nav-item]:px-5 [&_.rs-nav_.rs-nav-item]:py-[10px]',
  '[&_.rs-nav_.rs-nav-item]:text-[.9rem] [&_.rs-nav_.rs-nav-item]:font-semibold [&_.rs-nav_.rs-nav-item]:leading-[22.4px] [&_.rs-nav_.rs-nav-item]:text-black',
  '[&_.rs-nav_.rs-nav-item:focus]:bg-[var(--rs-primary-500)]',
  '[&_.rs-nav_.rs-nav-item-active]:!text-white [&_.rs-nav_.rs-nav-item-active]:bg-[var(--rs-primary-500)]',
  '[&_.rs-nav_.rs-nav-item:hover]:!text-white [&_.rs-nav_.rs-nav-item:hover]:bg-[var(--rs-primary-500)]',
].join(' ');

const SAVED_JOB_MAIN_CONTENT_CLASS = [
  '[&>.rs-col]:!mb-10',
  '[&_.rs-placeholder-graph]:mb-6 [&_.rs-placeholder-graph]:min-h-[390px] [&_.rs-placeholder-graph]:rounded-[20px]',
  '[&_.rs-placeholder-graph.loader-category]:!h-[120px] [&_.rs-placeholder-graph.loader-category]:max-h-[120px] [&_.rs-placeholder-graph.loader-category]:min-h-[20vh]',
  '[&_.search-results-row]:mb-6',
  '[&_.search-results-row>.rs-col]:px-3',
  'max-[768px]:[&_.search-results-row]:mb-0',
  'max-[768px]:[&_.search-results-row>.rs-col]:mb-6',
  '[&_.search-results-row_.rs-avatar-image]:bg-white [&_.search-results-row_.rs-avatar-image]:object-contain',
  '[&_.rs-input-group-addon]:mt-[9px] [&_.rs-input-group-addon]:!bg-transparent [&_.rs-input-group-addon]:!text-[#3F77C2]',
  '[&_.rs-input-group-addon:hover]:!bg-transparent [&_.rs-input-group-addon:hover]:!text-[#3F77C2]',
  '[&_.rs-input]:min-h-[54px] [&_.rs-input]:!py-[17.5px] [&_.rs-input]:!pl-9 [&_.rs-input]:!pr-5',
  '[&_.rs-picker-toggle]:min-h-[54px] [&_.rs-picker-toggle]:!py-[17.5px] [&_.rs-picker-toggle]:!pl-9 [&_.rs-picker-toggle]:!pr-5',
  '[&_.rs-picker-tag]:min-h-[58px] [&_.rs-picker-tag]:!px-5 [&_.rs-picker-tag]:!py-[9px] [&_.rs-picker-tag]:text-[#212121]',
  '[&_.rs-picker-tag_.rs-picker-toggle]:min-h-max [&_.rs-picker-tag_.rs-picker-toggle]:!p-[9px] [&_.rs-picker-tag_.rs-picker-toggle]:!pl-[17.5px]',
  '[&_.rs-picker-select]:w-full [&_.rs-picker-tag]:w-full',
].join(' ');

const SEARCH_RESULT_CARD_CLASS = [
  'max-h-[30em] rounded-[20px] bg-white p-4 text-[#E4E4E4]',
  'transition-all duration-[400ms] hover:shadow-[0_5px_10px_rgb(0_0_0_/_10%)]',
  '[&_svg]:text-[var(--rs-primary-500)]',
  '[&_p]:text-[100%] [&_p]:leading-[19.2px] [&_p]:text-[#595959]',
  '[&_p.salary-range]:text-[110%] [&_p.salary-range]:font-bold [&_p.salary-range]:leading-[22.4px] [&_p.salary-range]:text-[#212121]',
  '[&_.rs-stack]:w-full',
  '[&_.rs-stack>.rs-stack-item:nth-child(2)]:!w-full',
  '[&_.rs-stack>.rs-stack-item:nth-child(3)]:!w-full',
  '[&_.rs-stack>.rs-stack-item:last-child]:!w-full',
  '[&_.rs-stack>.rs-stack-item:nth-child(2)_.rs-stack]:!w-full',
  '[&_.rs-stack>.rs-stack-item:nth-child(2)_.rs-stack-item]:!w-full',
  '[&_.rs-stack>.rs-stack-item:nth-child(3)_.rs-stack]:!w-full',
  '[&_.rs-stack>.rs-stack-item:nth-child(3)_.rs-stack-item]:!w-full',
  '[&_.rs-stack>.rs-stack-item:last-child_.rs-stack]:!w-full',
  '[&_.rs-stack>.rs-stack-item:last-child_.rs-stack-item]:!w-full',
  '[&_.rs-col.company-info_p]:mb-[5px]',
  '[&_.rs-col.company-info_b]:overflow-hidden [&_.rs-col.company-info_b]:text-[1.2em] [&_.rs-col.company-info_b]:font-bold [&_.rs-col.company-info_b]:text-black',
  '[&_.rs-col.company-info_b]:line-clamp-2',
  '[&_.rs-btn-icon]:w-full [&_.rs-btn-icon]:px-2 [&_.rs-btn-icon]:py-4',
  '[&_.rs-btn-icon:disabled]:bg-[#E4E4E4] [&_.rs-btn-icon:disabled]:text-[#ABABAB] [&_.rs-btn-icon:disabled]:opacity-70',
  '[&_.rs-stack-item_.rs-stack-item:first-child_.rs-badge]:bg-[#E9A0FF]',
  '[&_.rs-stack-item_.rs-stack-item:nth-child(2)_.rs-badge]:bg-[#FFDE59]',
  '[&_.rs-stack-item_.rs-stack-item:nth-child(2)_.rs-badge.rs-badge-fulltime]:!bg-[#003884] [&_.rs-stack-item_.rs-stack-item:nth-child(2)_.rs-badge.rs-badge-fulltime]:!text-white',
  '[&_.rs-stack-item_.rs-stack-item:last-child_.rs-badge]:bg-[#90BFFF]',
  '[&_.rs-badge]:rounded-[20px] [&_.rs-badge]:px-[10px] [&_.rs-badge]:py-[2px]',
  '[&_.rs-badge]:whitespace-nowrap [&_.rs-badge]:font-normal [&_.rs-badge]:text-[#212121]',
].join(' ');
/* eslint-enable max-len */

type State = {
  tabKey: string;

  inprogressList: Array<any>;
  inprogressOpts: undefined | object;
  inprogressPage: number;
  totalInprogress: number;

  savedList: Array<any>;

  historyList: Array<any>;
  historyOpts: undefined | object;
  historyPage: number;
  totalHistory: number;

  // Saved property
  loading: boolean;
  savedHasMorePage: boolean;
  loadingCards: boolean;
};

class CandidateMyApplicationScreen extends React.Component<any, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      tabKey: 'inprogress',

      inprogressPage: 1,
      inprogressList: [],
      inprogressOpts: {},
      totalInprogress: 0,

      savedList: [],

      // eslint-disable-next-line react/no-unused-state
      historyPage: 1,
      historyList: [],
      historyOpts: {},
      totalHistory: 0,

      loadingCards: true,
      loading: true,
      savedHasMorePage: false,
    };
  }

  async componentDidMount(): Promise<void> {
    const t = _.first(document.getElementsByClassName('rs-content'));
    if (t) {
      _.set(t, 'style.backgroundColor', '#F6F6F6');
    }

    await Promise.allSettled([
      new Promise((resolve, reject) => {
        try {
          this.getInprogressApplications();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),

      new Promise((resolve, reject) => {
        try {
          this.getHistoryApplications();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),
    ]);
  }

  private getHistoryApplications = async (
    { historyPage } = this.state,
  ): Promise<void> => {
    this.setState({ loading: true });

    const response = await JobApplicationAPI.history(historyPage);
    const data = _.get(response, 'data') || [];
    const total = Number(_.get(response, 'meta.total') || 0);
    const historyOpts = _.get(response, 'meta.options');

    this.setState({
      historyOpts,
      loading: false,
      totalHistory: total,
      historyList: data,
    });
  };

  private getSavedApplications = async (page = 1): Promise<void> => {
    try {
      const response = await JobApplicationAPI.saved(page <= 1 ? 1 : page);
      const data = _.get(response, 'data') || [];
      const totalPage = Number(_.get(response, 'meta.totalPage') || 0);
      const total = Number(_.get(response, 'meta.total') || 0);

      this.setState({
        savedList: data,
        loadingCards: false,
        savedHasMorePage: page < totalPage && total > 0,
      });
    } catch (e) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  private getInprogressApplications = async (
    { inprogressPage } = this.state,
  ): Promise<void> => {
    this.setState({ loading: true });

    const response = await JobApplicationAPI.inprogress(inprogressPage);
    const data = _.get(response, 'data') || [];
    const inprogressOpts = _.get(response, 'meta.options');
    const totalInprogress = _.get(response, 'meta.total') || 0;

    this.setState({
      inprogressOpts,
      totalInprogress,
      loading: false,
      inprogressList: data,
    });
  };

  private onSaveOrRemoveApplication = async (
    jobId: number,
    isSaved: boolean,
  ): Promise<void> => {
    try {
      let message;

      if (isSaved) {
        await JobApplicationAPI.remove(jobId);
        message = 'Job posting has been removed from your bookmark list!';
      } else {
        await JobApplicationAPI.save(jobId);
        message = 'Job posting has been added to your bookmark list!';
      }

      toaster.push(util.buildMessage(message));

      this.getSavedApplications(1);
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  private redirectTo = (url: string): void => {
    const { navigate } = this.props;
    navigate(url, { replace: true });
  };

  renderSideTabs = ({ location } = this.props): JSX.Element => (
    <Col lg={6} md={6} sm={0} xs={0}>
      <Affix className="tg-affix-side-panel" top={window.innerHeight * 0.15}>
        <Stack spacing={6} direction="column">
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/home')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/home')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaHome />}
          >
            Home
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/search-companies')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/search-companies',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaBuilding />}
          >
            Company
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/search-jobs')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/search-jobs')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaSuitcase />}
          >
            Search Job
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/my-application')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/my-application',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaFileAlt />}
          >
            My Application
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/my-cv-maker')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/my-cv-maker')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaMagic />}
          >
            My CV Maker
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/profile')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/profile')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaUser />}
          >
            Profile
          </IconButton>
        </Stack>
      </Affix>
    </Col>
  );

  renderTab = (key: string): Array<JSX.Element> | JSX.Element | null => {
    switch (key) {
      case 'inprogress':
        return this.renderInProgressApplication();
      case 'saved':
        return this.renderSavedApplication();
      case 'history':
        return this.renderHistoryApplication();
      default:
        return null;
    }
  };

  private renderInProgressApplication = (): JSX.Element | null => {
    const {
      inprogressList,
      inprogressPage,
      inprogressOpts,
      totalInprogress,
      loading,
    } = this.state;

    return (
      <>
        {/* === Desktop: table view === */}
        <div className={TABLE_VIEW_CLASS}>
          <Table
            autoHeight
            loading={loading}
            rowHeight={(rowData: any) => {
              const readablestatus = _.get(inprogressOpts, rowData?.lastStatus);

              if (
                String(readablestatus).length <= 29 &&
                String(readablestatus).length >= 17
              ) {
                return 100;
              }

              if (String(readablestatus).length >= 29) {
                return 120;
              }
              return 80;
            }}
            data={inprogressList}
            bordered
            headerHeight={80}
            onRowClick={(rowData: any) => {
              const { navigate } = this.props;
              navigate(`/dashboard/candidate/my-application/${rowData.id}`);
            }}
          >
            <Table.Column flexGrow={1} align="center">
              <Table.HeaderCell>Date Applied</Table.HeaderCell>
              <Table.Cell>
                {(rowData) => {
                  return String(rowData.appliedDate).substring(0, 12);
                }}
              </Table.Cell>
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Company</Table.HeaderCell>
              <Table.Cell fullText>
                {(rowData) => {
                  return (
                    <Row style={{ marginTop: '-12px' }}>
                      <Col xs={8}>
                        <Avatar
                          circle
                          key={_.get(rowData, 'company.logo.url')}
                          size="md"
                          src={_.get(rowData, 'company.logo.url')}
                          alt="logo-startup"
                        />
                      </Col>
                      <Col
                        xs={16}
                        style={{ paddingTop: '12px', textAlign: 'left' }}
                      >
                        {_.get(rowData, 'company.name')}
                      </Col>
                    </Row>
                  );
                }}
              </Table.Cell>
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Position</Table.HeaderCell>
              <Table.Cell fullText dataKey="jobPosting.title" />
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.Cell
                fullText
                dataKey="jobPosting.employmentStatus.0.name"
              />
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.Cell>
                {(rowData) => {
                  return typeof inprogressOpts === 'object' &&
                    !_.isNull(inprogressOpts) ? (
                    <div
                      className={`btn-tg-pill-shaped ${
                        String(rowData.lastStatus).includes('ACCEPT') ||
                        String(rowData.lastStatus).includes('OFFER')
                          ? 'success'
                          : ''
                      } ${
                        rowData.lastStatus ===
                          'WAITING_FOR_TRANSFER_JOB_CONFIRMATION' && 'waiting'
                      }`}
                    >
                      {_.get(inprogressOpts, rowData.lastStatus)}
                    </div>
                  ) : (
                    ''
                  );
                }}
              </Table.Cell>
            </Table.Column>
          </Table>
        </div>

        {/* === Mobile: card view === */}
        <div className={CARD_VIEW_CLASS}>{this.renderInProgressCards()}</div>

        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={['total', '-', '|', 'pager']}
          total={totalInprogress}
          limit={10}
          activePage={inprogressPage}
          onChangePage={(page: number) =>
            this.setState({ inprogressPage: page }, () =>
              this.getInprogressApplications(),
            )
          }
        />
      </>
    );
  };

  private renderHistoryApplication = (): JSX.Element | null => {
    const { historyList, historyOpts, totalHistory, historyPage, loading } =
      this.state;

    return (
      <>
        {/* === Desktop: table view === */}
        <div className={TABLE_VIEW_CLASS}>
          <Table
            autoHeight
            rowHeight={(rowData: any) => {
              const readablestatus = _.get(historyOpts, rowData?.lastStatus);

              if (
                String(readablestatus).length <= 29 &&
                String(readablestatus).length >= 17
              ) {
                return 100;
              }

              if (String(readablestatus).length >= 29) {
                return 120;
              }
              return 80;
            }}
            loading={loading}
            data={historyList}
            bordered
            headerHeight={80}
            onRowClick={(rowData: any) => {
              const { navigate } = this.props;
              navigate(`/dashboard/candidate/my-application/${rowData.id}`);
            }}
          >
            <Table.Column flexGrow={1} align="center">
              <Table.HeaderCell>Date Applied</Table.HeaderCell>
              <Table.Cell>
                {(rowData) => {
                  return String(rowData.appliedDate).substring(0, 12);
                }}
              </Table.Cell>
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Company</Table.HeaderCell>
              <Table.Cell fullText>
                {(rowData) => {
                  return (
                    <Row style={{ marginTop: '-12px' }}>
                      <Col xs={8}>
                        <Avatar
                          circle
                          key={_.get(rowData, 'company.logo.url')}
                          size="md"
                          src={_.get(rowData, 'company.logo.url')}
                          alt="logo-startup"
                        />
                      </Col>
                      <Col
                        xs={16}
                        style={{ paddingTop: '12px', textAlign: 'left' }}
                      >
                        {_.get(rowData, 'company.name')}
                      </Col>
                    </Row>
                  );
                }}
              </Table.Cell>
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Position</Table.HeaderCell>
              <Table.Cell fullText dataKey="jobPosting.title" />
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.Cell
                fullText
                dataKey="jobPosting.employmentStatus.0.name"
              />
            </Table.Column>

            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.Cell>
                {(h) => {
                  return String(h.lastStatus).includes('REJECT') ||
                    String(h.lastStatus).includes('EXPIRE') ? (
                    <div className="btn-tg-pill-shaped failed">
                      {historyOpts ? _.get(historyOpts, h.lastStatus, '') : ''}
                    </div>
                  ) : (
                    <div className="btn-tg-pill-shaped success">
                      {historyOpts ? _.get(historyOpts, h.lastStatus, '') : ''}
                    </div>
                  );
                }}
              </Table.Cell>
            </Table.Column>
          </Table>
        </div>

        {/* === Mobile: card view === */}
        <div className={CARD_VIEW_CLASS}>{this.renderHistoryCards()}</div>

        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={['total', '-', '|', 'pager']}
          total={totalHistory}
          limit={10}
          activePage={historyPage}
          onChangePage={(page: number) => {
            this.setState({ historyPage: page }, () => {
              this.getHistoryApplications();
            });
          }}
        />
      </>
    );
  };

  private renderApplicationCard = (
    rowData: any,
    opts: any,
    isHistory = false,
  ): JSX.Element => {
    let statusClass = '';

    if (isHistory) {
      const isRejected =
        String(rowData.lastStatus).includes('REJECT') ||
        String(rowData.lastStatus).includes('EXPIRE');
      statusClass = isRejected ? 'failed' : 'success';
    } else {
      const isAccepted =
        String(rowData.lastStatus).includes('ACCEPT') ||
        String(rowData.lastStatus).includes('OFFER');
      if (isAccepted) {
        statusClass = 'success';
      } else if (
        rowData.lastStatus === 'WAITING_FOR_TRANSFER_JOB_CONFIRMATION'
      ) {
        statusClass = 'waiting';
      }
    }

    return (
      <Panel
        bordered
        className={APPLICATION_CARD_CLASS}
        key={rowData.id}
        onClick={() => {
          const { navigate } = this.props;
          navigate(`/dashboard/candidate/my-application/${rowData.id}`);
        }}
      >
        {/* Header: position + status pill */}
        <div className={APPLICATION_CARD_HEADER_CLASS}>
          <span className={APPLICATION_CARD_TITLE_CLASS}>
            {_.get(rowData, 'jobPosting.title')}
          </span>
          <div className={APPLICATION_CARD_STATUS_CLASS}>
            <div className={`btn-tg-pill-shaped ${statusClass}`}>
              {opts ? _.get(opts, rowData.lastStatus, '') : ''}
            </div>
          </div>
        </div>

        <hr className={APPLICATION_CARD_DIVIDER_CLASS} />

        {/* Company info */}
        <div className={APPLICATION_CARD_COMPANY_CLASS}>
          <Avatar
            circle
            size="sm"
            src={_.get(rowData, 'company.logo.url')}
            alt="logo"
          />
          <span className={APPLICATION_CARD_COMPANY_NAME_CLASS}>
            {_.get(rowData, 'company.name')}
          </span>
        </div>

        <hr className={APPLICATION_CARD_DIVIDER_CLASS} />

        {/* Detail rows */}
        <div className={APPLICATION_CARD_DETAILS_CLASS}>
          <div className={APPLICATION_CARD_ROW_CLASS}>
            <span className={APPLICATION_CARD_LABEL_CLASS}>Date Applied</span>
            <span className={APPLICATION_CARD_VALUE_CLASS}>
              {String(rowData.appliedDate).substring(0, 12)}
            </span>
          </div>
          <div className={APPLICATION_CARD_ROW_CLASS}>
            <span className={APPLICATION_CARD_LABEL_CLASS}>Type</span>
            <span className={APPLICATION_CARD_VALUE_CLASS}>
              {_.get(rowData, 'jobPosting.employmentStatus.0.name') || '-'}
            </span>
          </div>
        </div>
      </Panel>
    );
  };

  private renderInProgressCards = (): JSX.Element | null => {
    const { inprogressList, inprogressOpts, loading } = this.state;

    if (loading) {
      return (
        <div className={CARD_VIEW_STATE_CLASS}>
          <p className="no-results-text">Loading...</p>
        </div>
      );
    }

    if (!inprogressList.length) {
      return (
        <div className={CARD_VIEW_STATE_CLASS}>
          <p className="no-results-text">No applications found</p>
        </div>
      );
    }

    return (
      <div className={APPLICATION_CARDS_CLASS}>
        {_.map(inprogressList, (rowData) =>
          this.renderApplicationCard(rowData, inprogressOpts, false),
        )}
      </div>
    );
  };

  private renderHistoryCards = (): JSX.Element | null => {
    const { historyList, historyOpts, loading } = this.state;

    if (loading) {
      return (
        <div className={CARD_VIEW_STATE_CLASS}>
          <p className="no-results-text">Loading...</p>
        </div>
      );
    }

    if (!historyList.length) {
      return (
        <div className={CARD_VIEW_STATE_CLASS}>
          <p className="no-results-text">No applications found</p>
        </div>
      );
    }

    return (
      <div className={APPLICATION_CARDS_CLASS}>
        {_.map(historyList, (rowData) =>
          this.renderApplicationCard(rowData, historyOpts, true),
        )}
      </div>
    );
  };

  private renderSavedApplication = ():
    | Array<JSX.Element>
    | JSX.Element
    | null => {
    const {
      savedList,
      savedHasMorePage: hasMorePage,
      loadingCards,
    } = this.state;
    const { t } = this.props;
    const width = window.innerWidth;

    let size = 3;
    let threshold = 1 * window.innerHeight;
    if (width <= 425) {
      size = 1;
    } else if (width <= 1280) {
      size = 2;
    } else {
      threshold = 300;
      size = 3;
    }
    const chunked = _.chunk(savedList, size);

    return (
      <InfiniteScroll
        pageStart={0}
        threshold={threshold}
        loadMore={(page) => this.getSavedApplications(page)}
        hasMore={hasMorePage}
        className={`saved-job-main-content ${SAVED_JOB_MAIN_CONTENT_CLASS}`}
        loader={
          <FlexboxGrid key="load-more">
            <FlexboxGridItem
              key="load-more-item-1"
              as={Col}
              xs={24}
              sm={12}
              md={12}
              lg={8}
            >
              <PlaceholderGraph active />
            </FlexboxGridItem>
            <FlexboxGridItem
              key="load-more-item-2"
              as={Col}
              xs={24}
              sm={12}
              md={12}
              lg={8}
            >
              <PlaceholderGraph active />
            </FlexboxGridItem>
            <FlexboxGridItem
              key="load-more-item-3"
              as={Col}
              xs={24}
              sm={12}
              md={12}
              lg={8}
            >
              <PlaceholderGraph active />
            </FlexboxGridItem>
          </FlexboxGrid>
        }
      >
        {loadingCards && (
          <Row className="loader-container">
            {_.range(0, 6).map((i: number) => (
              <Col key={`loader-${i}`} xs={24} sm={12} md={12} lg={8}>
                <PlaceholderGraph active />
              </Col>
            ))}
          </Row>
        )}

        {!loadingCards && savedList.length === 0 && (
          <p className="no-results-text">No results</p>
        )}

        {_.map(chunked, (data: Array<any>, index: number) => (
          <Row
            className="search-results-row"
            key={`row-searchresults-${index}`}
          >
            {_.map(data, (o: any, idx: number) => {
              const obj = _.get(o, 'jobPosting');
              const employmentStatus = _.get(obj, 'employmentStatus.0.name');
              const company = _.get(obj, 'company');
              const companyLogo = _.get(obj, 'company.logo.url');
              const salaryHigh: number = _.get(obj, 'maxSalaryRange') || 0;
              const salaryLow: number = _.get(obj, 'minSalaryRange') || 0;
              const duration = _.get(obj, 'duration');
              const jobId = _.get(obj, 'id');
              const isSaved = _.get(obj, 'isSaved');
              const isHot = _.get(obj, 'isHot');
              const workingTypes: Array<any> = _.get(obj, 'workingTypes') || [];

              return (
                <Col
                  key={`col-searchresults-${idx}`}
                  xs={24}
                  sm={12}
                  md={12}
                  lg={8}
                >
                  <Panel
                    className={`search-result-card ${SEARCH_RESULT_CARD_CLASS}`}
                  >
                    <Stack
                      direction="column"
                      spacing={20}
                      style={{ alignItems: 'flex-start' }}
                    >
                      {/* Badge */}
                      <Stack spacing={8}>
                        {isHot ? <Badge content="New" /> : <span />}
                        <Badge
                          className={
                            employmentStatus === 'Full Time'
                              ? 'rs-badge-fulltime'
                              : ''
                          }
                          content={employmentStatus}
                        />
                        {workingTypes.map((val: any) => (
                          <Badge
                            key={`workingTypes-${val.name}`}
                            content={val.name}
                          />
                        ))}
                      </Stack>

                      {/* Company Information */}
                      <Stack spacing={6}>
                        <Row>
                          <Col xs={7}>
                            <Avatar
                              circle
                              size="lg"
                              src={companyLogo}
                              alt="logo-startup"
                            />
                          </Col>
                          <Col xs={17} className="company-info">
                            <Whisper
                              placement="topStart"
                              speaker={
                                <Tooltip>
                                  <b>{_.get(company, 'name')}</b>
                                </Tooltip>
                              }
                            >
                              <p>{_.get(company, 'name')}</p>
                            </Whisper>
                            <Whisper
                              placement="topStart"
                              speaker={
                                <Tooltip>
                                  <b>{_.get(obj, 'title')}</b>
                                </Tooltip>
                              }
                            >
                              <b>{_.get(obj, 'title')}</b>
                            </Whisper>
                          </Col>
                        </Row>
                      </Stack>

                      {/* Job Details */}
                      <Stack direction="column" spacing={6}>
                        <Row>
                          <Col xs={3}>
                            <FaMapMarkerAlt />
                          </Col>
                          <Col xs={21}>
                            <p>{_.get(company, 'region')}</p>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={3}>
                            <FaClock />
                          </Col>
                          <Col xs={21}>
                            {duration > 0 && <p>{duration} month(s)</p>}
                            {duration === 0 && <p>Full Time</p>}
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={3}>
                            <FaBuilding />
                          </Col>
                          <Col xs={21}>
                            <p>{_.get(company, 'industry.name')}</p>
                          </Col>
                        </Row>
                      </Stack>

                      {/* Salary range */}
                      <Stack spacing={6}>
                        <p className="salary-range">
                          {util.formatSalaryRange(salaryLow, salaryHigh)}
                        </p>
                      </Stack>

                      {/* Job Card Buttons */}
                      <Stack spacing={6}>
                        <Row>
                          <Col xs={19}>
                            <Button
                              className="btn-tg-primary"
                              block
                              disabled={!obj.active}
                              appearance="primary"
                              onClick={() => {
                                // eslint-disable-next-line max-len
                                const url = `/dashboard/candidate/jobs/${jobId}`;
                                window.open(url);
                              }}
                            >
                              {_.isEqual(obj.isApplied, true)
                                ? 'Applied'
                                : t(
                                    constants.translation.searchJob
                                      .formApplyButtonKey,
                                  )}
                            </Button>
                          </Col>
                          <Col xs={5}>
                            <IconButton
                              onClick={() =>
                                this.onSaveOrRemoveApplication(jobId, isSaved)
                              }
                              appearance="default"
                              size="lg"
                              disabled={!_.get(obj, 'active')}
                              icon={
                                isSaved ? (
                                  <img
                                    // eslint-disable-next-line max-len
                                    src={`${process.env.PUBLIC_URL}/bookmark-logo-filled.png`}
                                    alt="bookmark"
                                    width={18}
                                    height={24}
                                  />
                                ) : (
                                  <img
                                    // eslint-disable-next-line max-len
                                    src={`${process.env.PUBLIC_URL}/bookmark-logo.png`}
                                    alt="bookmark"
                                    width={18}
                                    height={24}
                                  />
                                )
                              }
                            />
                          </Col>
                        </Row>
                      </Stack>
                    </Stack>
                  </Panel>
                </Col>
              );
            })}
          </Row>
        ))}
      </InfiniteScroll>
    );
  };

  private onTabChanged = (tabKey: string): void => {
    this.setState({ tabKey });

    const { inprogressList, historyList } = this.state;

    if (tabKey === 'saved') {
      this.getSavedApplications();
    } else if (tabKey === 'history') {
      if (historyList.length === 0) {
        this.getHistoryApplications();
      }
    } else if (inprogressList.length === 0) {
      this.getInprogressApplications();
    }
  };

  renderMainContent = (): JSX.Element | null => {
    const { tabKey } = this.state;

    return (
      <Col md={24} sm={24} xs={24} lg={18}>
        <Col xs={24}>
          <div
            // eslint-disable-next-line max-len
            className={`myapplication-container ${MY_APPLICATION_CONTAINER_CLASS}`}
          >
            <Nav
              activeKey={tabKey}
              onSelect={this.onTabChanged}
              style={{ marginBottom: 50 }}
            >
              <Nav.Item eventKey="inprogress">In Progress</Nav.Item>
              <Nav.Item eventKey="history">History</Nav.Item>
              <Nav.Item eventKey="saved">Saved</Nav.Item>
            </Nav>

            {this.renderTab(tabKey)}
          </div>
        </Col>
      </Col>
    );
  };

  render(): JSX.Element | null {
    return (
      <Grid className="home-static-authorized-container" fluid>
        <Row>
          {this.renderSideTabs()}
          {this.renderMainContent()}
        </Row>
      </Grid>
    );
  }
}

export default withRouter(withTranslation()(CandidateMyApplicationScreen));
